import { Util } from "./util";

const DEFAULT_IDLE_RESET_MS = 3000;

/**
 * Announces once per empty -> populated transition, and again after
 * `idleResetMs` of inactivity even if the list never emptied. `getMessage` is re-read per
 * announce so a mid-session language switch doesn't go stale.
 */
export class SuggestionsAnnouncer {
    private readonly _getMessage: () => string;
    private readonly _idleResetMs: number;
    private _announced = false;
    private _idleTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(getMessage: () => string, idleResetMs = DEFAULT_IDLE_RESET_MS) {
        this._getMessage = getMessage;
        this._idleResetMs = idleResetMs;
    }

    public update(hasSuggestions: boolean): void {
        if (this._idleTimer !== null) {
            clearTimeout(this._idleTimer);
            this._idleTimer = null;
        }

        if (!hasSuggestions) {
            this._announced = false;
            return;
        }

        if (!this._announced) {
            this._announced = true;
            Util.announce(this._getMessage());
        }

        this._idleTimer = setTimeout(() => {
            this._announced = false;
            this._idleTimer = null;
        }, this._idleResetMs);
    }
}

/**
 * Suggestion lists the app doesn't build itself (Google's `.pac-container`, HERE's
 * `.geo-suggest-dropdown`). Multiple can exist at once, e.g. Locations' store-search box and
 * its embedded delivery-address input, so each matching container is tracked separately.
 */
const GEO_SUGGESTION_LISTS = [
    { container: ".pac-container", item: ".pac-item" },
    { container: ".geo-suggest-dropdown", item: ".geo-suggest-item" }
];

const registeredSelectors = new Set<string>();

function isVisible(element: HTMLElement): boolean {
    const style = getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
}

function trackContainers(containerSelector: string, getMessage: () => string, itemSelector: string): void {
    const tracked = new Map<HTMLElement, MutationObserver>();

    const track = (container: HTMLElement): void => {
        if (tracked.has(container))
            return;

        const announcer = new SuggestionsAnnouncer(getMessage);
        // Providers re-render rows on every keystroke, so read list state, not the mutation itself.
        const evaluate = (): void =>
            announcer.update(isVisible(container) && container.querySelectorAll(itemSelector).length > 0);

        const observer = new MutationObserver(evaluate);
        observer.observe(container, { childList: true, attributes: true, attributeFilter: ["style"] });

        tracked.set(container, observer);
        evaluate();
    };

    const scan = (): void => {
        for (const [container, observer] of tracked) {
            if (!container.isConnected) {
                observer.disconnect();
                tracked.delete(container);
            }
        }

        document.querySelectorAll<HTMLElement>(containerSelector).forEach(track);
    };

    scan();
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
}

export function initGeoSuggestionAnnouncements(getMessage: () => string): void {
    for (const list of GEO_SUGGESTION_LISTS) {
        if (registeredSelectors.has(list.container))
            continue;

        registeredSelectors.add(list.container);
        trackContainers(list.container, getMessage, list.item);
    }
}
